import * as v from 'valibot';

import { DynamicData, Stage } from '../common.types';
import { Documents } from '../shared/documents.types';
import { ReportStatus } from '../shared/report.types';

const RiskStatus = v.picklist([
	// Draft status
	'Risk In Progress',
	// Active statuses
	'Pending Underwriter',
	'Calculation Complete',

	// Closed
	'Closed',
]);
export type RiskStatus = v.InferOutput<typeof RiskStatus>;

const riskStage: Record<RiskStatus, Stage> = {
	'Risk In Progress': 'Draft',

	'Pending Underwriter': 'Active',
	'Calculation Complete': 'Active',

	Closed: 'Closed',
};

export const Risk = v.pipe(
	v.object({
		riskId: v.number(),
		riskReferenceNumber: v.nullish(v.string()),
		riskCreated: v.string(),
		businessName: v.nullish(v.string()),
		noprCompleted: v.boolean(),
		underwriter: v.nullish(v.string()),
		riskStatus: RiskStatus,
		underwriterId: v.nullish(v.string()),
		riskStatusId: v.picklist([1, 2, 3, 4]), // Risk In Progress, Pending Underwriter, Closed, Calculation Complete
		fileStatus: v.custom<`${number}/${number}`>(
			(input) => typeof input === 'string' && /^\d+\/\d+$/.test(input),
		),
	}),
	v.transform((data) => ({
		...data,
		stage: riskStage[data.riskStatus],
	})),
);

export type Risk = v.InferOutput<typeof Risk>;
export type RiskResponse = v.InferInput<typeof Risk>;

export const RisksResults = v.object({
	results: v.array(Risk),
	count: v.number(),
});
export type RisksResults = v.InferOutput<typeof RisksResults>;
export type RisksResultsResponse = v.InferInput<typeof RisksResults>;

export const RiskFilters = v.object({
	riskStatusIds: v.optional(v.array(v.number())),
	fromRiskCreated: v.optional(v.string()),
	toRiskCreated: v.optional(v.string()),
	underwriterIds: v.optional(v.array(v.string())),
	hideClosed: v.optional(v.boolean()),
});

export type RiskFilters = v.InferOutput<typeof RiskFilters>;

export const RisksQuery = v.object({
	skip: v.number(),
	take: v.number(),
	searchText: v.optional(v.string()),
	...RiskFilters.entries,
});

export type RisksQuery = v.InferOutput<typeof RisksQuery>;

const RiskDetails = v.object({
	naicsCodeId: v.nullish(v.number()),
	businessName: v.nullish(v.string()),
	storeNumber: v.nullish(v.string()),
	addressLine1: v.nullish(v.string()),
	addressLine2: v.nullish(v.string()),
	town: v.nullish(v.string()),
	stateId: v.nullish(v.string()),
	countryId: v.nullish(v.string()),
	zipCode: v.nullish(v.string()),
	hasSalesVariation: v.nullish(v.boolean()),
	variationType: v.nullish(v.boolean()),
	variation: v.nullish(v.number()),
});

export const RiskValues = v.object({
	riskReferenceNumber: v.nullish(v.string()),
	organisationId: v.nullish(v.number()),
	...RiskDetails.entries,
});

export type RiskValues = v.InferOutput<typeof RiskValues>;

export const SingleRisk = v.pipe(
	v.object({
		riskId: v.number(),
		riskReferenceNumber: v.nullish(v.string()),
		riskCreated: v.string(),
		riskStatusId: v.picklist([1, 2, 3, 4]), // Risk In Progress, Pending Underwriter, Closed, Calculation Complete
		riskStatus: RiskStatus,
		noprCompleted: v.boolean(),
		organisationId: v.nullish(v.number()),
		riskInsured: v.object({
			naicsCodeId: v.nullish(v.number()),
			naicsCode: v.nullish(v.string()),
			businessName: v.nullish(v.string()),
			storeNumber: v.nullish(v.string()),
			addressLine1: v.nullish(v.string()),
			addressLine2: v.nullish(v.string()),
			town: v.nullish(v.string()),
			state: v.nullish(v.string()),
			stateId: v.nullish(v.string()),
			country: v.nullish(v.string()),
			countryId: v.nullish(v.string()),
			zipCode: v.nullish(v.string()),
			underwriter: v.nullish(v.string()),
			underwriterId: v.nullish(v.string()),
			underwriterEmail: v.nullish(v.string()),
			underwriterPhoneNumber: v.nullish(v.string()),
			insuredAddress: v.nullish(v.string()),
			hasSalesVariation: v.nullish(v.boolean()),
			variationType: v.nullish(v.boolean()),
			variation: v.nullish(v.number()),
		}),
		riskDetails: DynamicData,
		riskDocuments: Documents,
		reportStatus: ReportStatus,
	}),
	v.transform((data) => ({
		...data,
		stage: riskStage[data.riskStatus],
	})),
);

export type SingleRisk = v.InferOutput<typeof SingleRisk>;
export type SingleRiskResponse = v.InferInput<typeof SingleRisk>;

export const RiskBody = v.object({
	isDraft: v.boolean(),
	riskId: v.number(),
	riskReferenceNumber: v.nullish(v.string()),
	organisationId: v.nullish(v.number()),
	riskInsured: v.object({ ...RiskDetails.entries }),
});

export type RiskBody = v.InferOutput<typeof RiskBody>;

export const PutRiskResponse = v.object({
	errors: v.nullish(
		v.array(
			v.object({
				propertyName: v.string(),
				errorMessage: v.string(),
			}),
		),
	),
});

export type PutRiskResponse = v.InferOutput<typeof PutRiskResponse>;

export const CloseRiskBody = v.object({
	closureReasonId: v.number(),
	closureReason: v.nullable(v.string()),
});

export type CloseRiskBody = v.InferOutput<typeof CloseRiskBody>;

export const ReopenRiskBody = v.object({
	reopenReasonId: v.number(),
	reopenReason: v.nullable(v.string()),
});

export type ReopenRiskBody = v.InferOutput<typeof ReopenRiskBody>;

export const NewRiskResponse = v.object({
	riskId: v.number(),
});

export type NewRiskResponse = v.InferOutput<typeof NewRiskResponse>;

export const RiskStatuses = v.array(
	v.object({
		id: v.number(),
		name: v.string(),
	}),
);

export type RiskStatuses = v.InferOutput<typeof RiskStatuses>;
